import {
    Directive,
    OnInit,
    OnDestroy,
    Input,
    ElementRef,
    HostListener,
    AfterViewInit,
    Optional,
    Renderer2,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';
import { NgControl } from '@angular/forms';

@Directive({
    // tslint:disable-next-line:directive-selector
    selector: 'textarea[autoResize]',
})
export class AutoResizeDirective implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    private defaultRows: number = 1;
    private destroy$: Subject<boolean> = new Subject<boolean>();

    @Input()
    public get autoResize(): boolean {
        return this._autoResize;
    }
    public set autoResize(state: boolean) {
        this._autoResize = coerceBooleanProperty(state);
    }
    private _autoResize: boolean = true;

    @Input()
    set minRows(minRows: number) {
        this.defaultRows = coerceNumberProperty(minRows);
    }

    constructor(
        protected el: ElementRef,
        protected renderer: Renderer2,
        @Optional() protected control: NgControl) {
    }

    @HostListener('input', ['$event.target'])
    onInput(): void {
        this.resize();
    }

    ngOnInit(): void {
        fromEvent(window, 'resize')
            .pipe(takeUntil(this.destroy$), debounceTime(300))
            .subscribe(() => this.resize());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['autoResize']) {
            const enable = changes['autoResize'].currentValue || this.autoResize;
            this.renderer.setProperty(this.el.nativeElement, 'rows', this.defaultRows);
            this.renderer.setStyle(this.el.nativeElement, 'overflow', enable ? 'hidden' : '');
            this.renderer.setStyle(this.el.nativeElement, 'resize', enable ? 'none' : '');
            if (enable) {
                this.resize();
            }
        }
    }

    ngAfterViewInit(): void {
        if (this.autoResize) {
            if (this.control) {
                this.handleFormControlChanges();
            } else {
                this.resize();
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.complete();
        this.destroy$.unsubscribe();
    }

    /**
     * Calculates the height and set it, causing the textarea to resize.
     */
    public resize(heightAuto = true): void {
        if (this.autoResize) {
            // clone element
            const clone = this.el.nativeElement.cloneNode(true);
            const parent = this.el.nativeElement.parentNode;
            clone.style.width = this.el.nativeElement.offsetWidth + 'px';
            clone.style.visibility = 'hidden';
            clone.style.position = 'absolute';
            clone.textContent = this.el.nativeElement.value;
            parent.appendChild(clone);
            clone.style['overflow-y'] = 'hidden';
            clone.style.height = 'auto';
            let cloneHeight = clone.scrollHeight;

            this.renderer.setStyle(clone, 'height', cloneHeight);
            // calculate height
            let { offsetHeight, scrollHeight } = clone;
            let height = (offsetHeight > scrollHeight ? offsetHeight : scrollHeight);
            height = height <= 0 ? 'auto' : `${height}px`;
            // remove clone
            parent.removeChild(clone);
            // set height
            this.renderer.setStyle(this.el.nativeElement, 'height', height);
        }
    }

    /**
     * handles value changes through FormControl
     * @protected
     */
    protected handleFormControlChanges(): void {
        // in case value changed manually through control, call resize()
        // startWith is being used to cause resize to be called in case of late subscription after value has changed
        this.control.valueChanges.pipe(takeUntil(this.destroy$), startWith('')).subscribe(() => this.resize());
    }

}
